import { Component, inject, Injector, OnInit, ViewChild } from '@angular/core';
import { DialogBaseComponent } from '../../base-admin-panel/dialog-base/dialog-base.component';
import { CompaniesService, CompanyModel, ICompanyCreateModel, IExternalAPIViewModel } from "../../../../services/companies.service";
import { forkJoin, map, of, switchMap, tap } from 'rxjs';
import { SettingsService, TimezoneViewModel } from 'app/services/settings.service';
import { AccessRights } from "../../../../services/users.service";
import { ContainersService } from 'app/services/containers.service';
import { ContainerViewModel } from '../../ContainerViewModel';
import { ContainerAccessComponent } from 'app/system/shared/components/container-access/container-access.component';
import { matchesSearchRule } from "../../../shared/pipes/filterByProperty";

@Component({
    selector: 'app-companies-dialog-base',
    templateUrl: './companies-dialog-base.component.html',
    styleUrls: ['./companies-dialog-base.component.css'],
    standalone: false
})
export class CompaniesDialogBaseComponent<T extends ICompanyCreateModel> extends DialogBaseComponent<T> implements OnInit {
  public companies: CompanyModel[] = [];
  public externalAPI: IExternalAPIViewModel;
  public timezones: TimezoneViewModel[] = [];
  public currentCompany: CompanyModel;
  public companyContainers: ContainerViewModel[] = [];
  public containersFilter: string = "";
  public selectedContainer: ContainerViewModel | null = null;
  public isLoading: boolean = true;
  private companiesService: CompaniesService;
  private containersService: ContainersService;

  public openedTabName: string = "companyData";
  private settingsService: SettingsService = inject(SettingsService);
  protected showParentCompanyField: boolean = true;
  @ViewChild(ContainerAccessComponent, { static: false }) private containerAccessDialog?: ContainerAccessComponent;

  constructor(protected injector: Injector) {
    super(injector);
    this.externalAPI = {
      telegramSettings: {
        token: "",
        enabled: false
      },
      emailSettings: {
        smtpHost: "",
        smtpPort: 0,
        ssl: false,
        user: "",
        from: "",
        password: "",
        text: "",
        enabled: false
      },
      companyId: 0
    };
    this.companiesService = injector.get(CompaniesService);
    this.containersService = injector.get(ContainersService);
  }

  public openTab(name: string) {
    this.openedTabName = name;
    if (name === "containers") {
      this.loadCompanyContainers();
    }
  }

  public isSelected(name: string) {
    return name == this.openedTabName;
  }

  protected isValid(): boolean {
    if (this.model.ParentCompanyId == undefined) {
      this.toastr.error(this.translate.instant("Parent company required"));
      return;
    }


    return true;
  }

  ngOnInit() {
    super.ngOnInit();
    let id = (this.model as { Id?: number })?.Id ?? 0;

    const companyDetails$ = id > 0
      ? this.companiesService.getCompany(id).pipe(
        tap(company => {
          this.model = company as any;
          this.currentCompany = company as CompanyModel;
          if (this.openedTabName === "containers") {
            this.loadCompanyContainers();
          }
        }),
        switchMap(company =>
          this.companiesService.getExternalAPIsByCompanyId(id).pipe(
            map(externalAPI => ({ company, externalAPI }))
          )
        )
      )
      : of(null);

    forkJoin({
      timezones: this.settingsService.getTimezones(),
      companies: this.companiesService.getCompanies(),
      companyDetails: companyDetails$
    }).subscribe(result => {
      this.timezones = result.timezones;
      if (this.model.TimeZone == "") {
        const zeroTimezone = this.timezones.find(tz => tz.Offset === 0);
        if (zeroTimezone) {
          this.model.TimeZone = zeroTimezone.Name;
        }
      }

      this.companies = id
        ? result.companies.filter(x => x.Id != id)
        : result.companies;

      const externalApi = result.companyDetails?.externalAPI;
      if (externalApi) {
        if (externalApi.emailSettings) this.externalAPI.emailSettings = externalApi.emailSettings;
        if (externalApi.telegramSettings) this.externalAPI.telegramSettings = externalApi.telegramSettings;
        this.externalAPI.companyId = externalApi.companyId;
      }

      this.isLoading = false;
    }, () => {
      this.isLoading = false;
    });
  }

  public get filteredCompanyContainers(): ContainerViewModel[] {
    const term = (this.containersFilter || "").trim();
    if (!term) return this.companyContainers;
    return this.companyContainers.filter(container => matchesSearchRule(container.Name, term));
  }

 
  public get canDeleteContainers(): boolean {
    return this.usersService.IsPermitted(AccessRights.EDITUSERS)
      || this.usersService.IsPermitted(AccessRights.EDIT_ALL_USERS);
  }

  public selectContainer(container: ContainerViewModel) {
    this.selectedContainer = container;
  }

  public openCreateContainer() {
    this.containerAccessDialog?.openDialog();
  }

  public openEditContainer(container?: ContainerViewModel | null, event?: Event) {
    if (event) event.stopPropagation();
    const target = container ?? this.selectedContainer;
    if (!target) return;
    this.selectedContainer = target;
    this.containerAccessDialog?.openDialog(target);
  }

  public deleteContainer(container?: ContainerViewModel | null, event?: Event) {
    if (event) event.stopPropagation();
    if (!this.canDeleteContainers) return;
    const target = container ?? this.selectedContainer;
    if (!target?.Id) return;
    if (!confirm(this.translate.instant("Delete") + " " + target.Name + "?")) return;

    this.containersService.DeleteContainer(target.Id).subscribe(() => {
      this.toastr.success(this.translate.instant("Container deleted"));
      if (this.selectedContainer?.Id === target.Id) {
        this.selectedContainer = null;
      }
      this.loadCompanyContainers();
    });
  }

  private loadCompanyContainers(selectedContainerId: number = 0) {
    const companyId = this.currentCompany?.Id ?? (this.model as { Id?: number })?.Id ?? 0;
    if (!companyId) {
      this.companyContainers = [];
      this.selectedContainer = null;
      return;
    }

    this.containersService.GetByCompanyId(companyId).subscribe(containers => {
      this.companyContainers = [...(containers ?? [])]
        .sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
      if (selectedContainerId > 0) {
        this.selectedContainer = this.companyContainers.find(container => container.Id === selectedContainerId) ?? null;
        return;
      }
      if (this.selectedContainer?.Id) {
        this.selectedContainer = this.companyContainers.find(container => container.Id === this.selectedContainer?.Id) ?? null;
      }
    });
  }

  public onContainersChanged() {
    this.loadCompanyContainers();
  }
}

